import * as React from 'react';
import { Box, SxProps, Theme } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ActionButton } from '@europrocurement/flexy-components/components/atoms';
import { type ActionButtonVariant } from '@europrocurement/flexy-components/components/atoms/FlexyButton';
import { NamedIconsType } from '@europrocurement/l2d-icons';
import { faChevronRight } from '@fortawesome/sharp-regular-svg-icons';
import { useL2DTheme } from '../../../../hooks/useL2DTheme';

export type { ActionButtonVariant };

/**
 * Type d'action déclenchée par le bouton.
 * Permet d'indiquer visuellement à l'utilisateur le comportement attendu.
 */
export type HeaderActionType = 'modal' | 'navigation';

export interface HeaderActionButtonProps {
    /** Libellé affiché sur le bouton */
    label: string;

    /** Icône à afficher avant le libellé */
    icon?: NamedIconsType;

    /** Callback exécuté lors du clic sur le bouton */
    onClick: () => void;

    /**
     * Type d'action déclenchée par le bouton.
     * - 'modal' : ouvre une modal (reste sur la page actuelle)
     * - 'navigation' : redirige vers une autre page
     * @default 'modal'
     */
    actionType?: HeaderActionType;

    /**
     * Indique si le bouton est désactivé
     * @default false
     */
    disabled?: boolean;

    /**
     * Indique si une opération est en cours
     * @default false
     */
    loading?: boolean;

    /**
     * Variante visuelle du bouton
     * @default 'primary'
     */
    variant?: ActionButtonVariant;

    /** Styles additionnels à appliquer au bouton */
    sx?: SxProps<Theme>;
}

const baseStyles: SxProps<Theme> = {
    alignItems: 'center',
    padding: '12px 16px',
    borderRadius: '12px',
    lineHeight: '24px',
    fontFamily: 'Inter',
    fontSize: '16px',
    width: 'max-content',
    fontWeight: 500,
    display: 'flex',
    flexDirection: 'row',
    gap: '10px',
};

/**
 * Bouton d'action générique pour les en-têtes de listing.
 *
 * Gère deux types d'actions avec indication visuelle distincte :
 * - Modal : ouvre un formulaire sans quitter la page
 * - Navigation : redirige vers une page dédiée (indiqué par une flèche)
 *
 * @example
 * ```tsx
 * // Bouton ouvrant une modal (par défaut)
 * <HeaderActionButton
 *     label="Nouveau rappel"
 *     icon={reminderIcon}
 *     onClick={() => modalRef.current?.open()}
 * />
 *
 * // Bouton de navigation (avec indicateur visuel)
 * <HeaderActionButton
 *     label="Nouveau prescripteur"
 *     icon={createPrescriberIcon}
 *     actionType="navigation"
 *     onClick={() => navigate(PRESCRIBER_NEW)}
 * />
 * ```
 */
const HeaderActionButton: React.FunctionComponent<HeaderActionButtonProps> = function ({
    label,
    icon,
    onClick,
    actionType = 'modal',
    disabled = false,
    loading = false,
    variant = 'primary',
    sx,
}: HeaderActionButtonProps) {
    const { palette } = useL2DTheme();
    const token = palette.actionButton[variant] ?? palette.actionButton.primary;

    const computedStyles = React.useMemo(
        () => ({
            ...baseStyles,
            backgroundColor: token?.backgroundColor,
            color: token?.textColor,
            ':hover': {
                backgroundColor: token?.backgroundColorOnHover,
                color: token?.textColorOnHover,
            },
            ...sx,
        }),
        [token, sx],
    );

    const handleClick = React.useCallback(() => {
        if (!disabled && !loading) {
            onClick();
        }
    }, [disabled, loading, onClick]);

    const buttonLabel = React.useMemo(() => {
        if (actionType === 'navigation') {
            return (
                <Box
                    component="span"
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                    }}
                >
                    {label}
                    <FontAwesomeIcon icon={faChevronRight} />
                </Box>
            );
        }
        return label;
    }, [actionType, label]);

    return (
        <ActionButton
            label={buttonLabel}
            icon={icon}
            action={handleClick}
            disabled={disabled || loading}
            sx={computedStyles}
        />
    );
};

export default HeaderActionButton;
